// src/pages/pmc/PmcEngineerApprovals.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-slate-100 text-slate-700",
    blue: "bg-sky-100 text-sky-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

// safe timestamp → date
const safeToDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  return null;
};

export default function PmcEngineerApprovals() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);

  const [statusFilter, setStatusFilter] = useState("PENDING"); // PENDING | APPROVED | REJECTED | ALL
  const [qText, setQText] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ---------- AUTH GUARD ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login to PMC dashboard.");
        navigate("/pmc/dashboard", { replace: true });
        return;
      }
      setUser(u);
    });
    return () => unsub();
  }, [auth, navigate]);

  /* ---------- LOAD ENGINEERS FROM engineers COLLECTION ✅ ---------- */
  useEffect(() => {
    const load = async () => {
      if (!user) return;
      try {
        setLoading(true);

        // ✅ Engineers are stored in: engineers/{uid}
        const snap = await getDocs(collection(db, "engineers"));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

        // newest first (createdAt)
        list.sort((a, b) => {
          const ta = a.createdAt?.seconds || 0;
          const tb = b.createdAt?.seconds || 0;
          return tb - ta;
        });

        setRows(list);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load engineers.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  /* ---------- FILTERED LIST ---------- */
  const filtered = useMemo(() => {
    let list = [...rows];

    if (statusFilter !== "ALL") {
      list = list.filter(
        (r) =>
          (r.status || "pending").toLowerCase() ===
          statusFilter.toLowerCase()
      );
    }

    const needle = qText.trim().toLowerCase();
    if (needle) {
      list = list.filter((r) => {
        const hay = [
          r.fullName,
          r.email,
          r.mobile,
          r.phone,
          r.id,
          r.role,
          r.status,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");
        return hay.includes(needle);
      });
    }

    return list;
  }, [rows, statusFilter, qText]);

  /* ---------- ACTIONS ---------- */
  const updateEngineer = async (uid, patch) => {
    try {
      // ✅ update engineers/{uid}
      const ref = doc(db, "engineers", uid);
      await updateDoc(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
      });

      setRows((prev) =>
        prev.map((r) => (r.id === uid ? { ...r, ...patch } : r))
      );
    } catch (e) {
      console.error(e);
      toast.error("Update failed (permission / rules).");
    }
  };

  const handleApprove = async (r) => {
    await updateEngineer(r.id, {
      status: "approved",
      approvedAt: serverTimestamp(),
      approvedByUid: user.uid,
      rejectedAt: null,
      rejectedByUid: null,
    });
    toast.success("Engineer approved.");
  };

  const handleReject = async (r) => {
    await updateEngineer(r.id, {
      status: "rejected",
      rejectedAt: serverTimestamp(),
      rejectedByUid: user.uid,
      approvedAt: null,
      approvedByUid: null,
    });
    toast.success("Engineer rejected.");
  };

  /* ---------- RENDER ---------- */
  if (!user) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="text-sm text-slate-600">Loading…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-emerald-50 pb-10">
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
          {/* HEADER */}
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-[0.18em]">
                PMC / Admin
              </div>
              <div className="mt-1 text-lg font-extrabold text-emerald-800">
                Engineer Approvals
              </div>
              <div className="mt-1 text-[11px] text-slate-600">
                Engineers registered via Engineer Portal (engineers collection)
                will appear here for approval.
              </div>
            </div>
            <div className="text-right text-[11px] text-slate-600 space-y-1">
              <button
                type="button"
                onClick={() => navigate("/pmc/dashboard")}
                className="text-emerald-700 underline"
              >
                ← Back to PMC Dashboard
              </button>
            </div>
          </div>

          {/* FILTERS */}
          <div className="rounded-2xl bg-white shadow border border-emerald-100 p-4">
            <div className="grid md:grid-cols-3 gap-3 text-[12px]">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border rounded-lg bg-white text-[12px]"
                >
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="ALL">All</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600">
                  Search (name / email / phone)
                </label>
                <input
                  value={qText}
                  onChange={(e) => setQText(e.target.value)}
                  placeholder="e.g. Rahul / eng@example.com / 98xxxxxx"
                  className="mt-1 w-full px-3 py-1.5 border rounded-lg bg-white text-[12px]"
                />
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="rounded-2xl bg-white shadow border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 bg-slate-900 text-white text-[11px] font-semibold flex items-center justify-between">
              <span>Engineers ({filtered.length})</span>
              <Link to="/" className="text-[11px] underline">
                Back to Main Site
              </Link>
            </div>

            {loading ? (
              <div className="px-4 py-4 text-[12px] text-slate-500">
                Loading engineers…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-4 text-[12px] text-slate-500">
                No engineers found for current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px] border-collapse">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-200 px-2 py-2 text-left w-10">
                        #
                      </th>
                      <th className="border border-slate-200 px-2 py-2 text-left">
                        Engineer
                      </th>
                      <th className="border border-slate-200 px-2 py-2 text-left w-40">
                        Contact
                      </th>
                      <th className="border border-slate-200 px-2 py-2 text-left w-28">
                        Status
                      </th>
                      <th className="border border-slate-200 px-2 py-2 text-left w-40">
                        Registered
                      </th>
                      <th className="border border-slate-200 px-2 py-2 text-left w-36">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.map((r, idx) => {
                      const status = (r.status || "pending").toLowerCase();
                      const statusColor =
                        status === "approved"
                          ? "green"
                          : status === "rejected"
                          ? "red"
                          : "yellow";

                      const created = safeToDate(r.createdAt);
                      const createdStr = created
                        ? dayjs(created).format("DD/MM/YYYY, hh:mm A")
                        : "—";

                      return (
                        <tr
                          key={r.id}
                          className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}
                        >
                          <td className="border border-slate-200 px-2 py-2">
                            {idx + 1}
                          </td>

                          <td className="border border-slate-200 px-2 py-2">
                            <div className="font-semibold text-slate-900">
                              {r.fullName || "—"}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              UID: {r.id}
                            </div>
                          </td>

                          <td className="border border-slate-200 px-2 py-2">
                            <div className="text-slate-700">{r.email || "—"}</div>
                            <div className="text-[10px] text-slate-500">
                              {r.mobile || r.phone || "—"}
                            </div>
                          </td>

                          <td className="border border-slate-200 px-2 py-2">
                            <Badge color={statusColor}>{status.toUpperCase()}</Badge>
                          </td>

                          <td className="border border-slate-200 px-2 py-2">
                            {createdStr}
                          </td>

                          <td className="border border-slate-200 px-2 py-2">
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() => handleApprove(r)}
                                disabled={status === "approved"}
                                className={cx(
                                  "px-2 py-1 rounded text-[10px] font-semibold",
                                  status === "approved"
                                    ? "bg-emerald-200 text-white cursor-not-allowed"
                                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                                )}
                              >
                                Approve
                              </button>

                              <button
                                type="button"
                                onClick={() => handleReject(r)}
                                disabled={status === "rejected"}
                                className={cx(
                                  "px-2 py-1 rounded text-[10px] font-semibold",
                                  status === "rejected"
                                    ? "bg-red-200 text-white cursor-not-allowed"
                                    : "bg-red-600 text-white hover:bg-red-700"
                                )}
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
